/**
 * Factories for the MutationCache.onError / QueryCache.onError handlers used
 * in main.tsx.
 *
 * Extracted so tests can import and exercise the REAL handlers rather than
 * hand-rolled copies — a copy-based test won't catch deletion/breakage of
 * the real wiring.
 *
 * Both handlers check `meta.suppressErrorToast` first: a call site that sets
 * `meta: { suppressErrorToast: true }` on its `useMutation`/`useQuery` opts
 * out of the global toast and owns its own error presentation (state/inline
 * UI) instead. This lets a call site keep the throwing `apiFetch` contract
 * without being forced onto `apiFetchRaw` purely for presentation reasons.
 */
import type { Mutation, Query } from '@tanstack/react-query';
import { ApiProblemError, WriteRefusedError } from './apiProblemError';
import { notifyError } from './errors';
import { DEMO_GATE_DENIED_CODE } from '@shared/schemas/demo-gate';

// Register augmentation: types `meta` on useMutation/useQuery so
// `suppressErrorToast` gets autocomplete and typos are caught. Additive —
// no call site currently passes `meta`, so this cannot break existing types.
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { suppressErrorToast?: boolean };
    queryMeta: { suppressErrorToast?: boolean };
  }
}

export function makeMutationCacheOnError(): (
  err: unknown,
  variables?: unknown,
  context?: unknown,
  mutation?: Mutation<unknown, unknown, unknown>,
) => void {
  return (err, _variables, _context, mutation) => {
    if (mutation?.meta?.suppressErrorToast) return;
    // `apiFetch` already announced it.
    if (err instanceof WriteRefusedError) return;
    if (err instanceof ApiProblemError && err.code === DEMO_GATE_DENIED_CODE) return;
    try {
      notifyError(err, 'mutation');
    } catch {}
  };
}

export function makeQueryCacheOnError(): (
  err: unknown,
  query?: Query<unknown, unknown, unknown>,
) => void {
  return (err, query) => {
    if (query?.meta?.suppressErrorToast) return;
    if (err instanceof WriteRefusedError) return;
    if (err instanceof ApiProblemError && err.code === DEMO_GATE_DENIED_CODE) return;
    try {
      notifyError(err, 'query');
    } catch {}
  };
}
